import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Reveal } from "@/lib/reveal";
import { submitContact, submitNetlifyForm } from "@/lib/api";
import { Mail, Phone, AlertCircle, Check, Lock } from "lucide-react";
import TikTokIcon from "@/components/icons/TikTokIcon";
import { BRAND } from "@/data/founder";
import { usePageMeta } from "@/lib/usePageMeta";

const FAQS = [
  {
    q: "Is this therapy?",
    a: "No. Calm & Connected is lived-experience support — companionship, conversation, and a steady presence. It is not therapy, counseling, or medical advice.",
  },
  {
    q: "How do sessions happen?",
    a: "Sessions can be virtual (phone or video) or in person, depending on what feels best for you and what's possible logistically.",
  },
  {
    q: "What if I'm in crisis?",
    a: "If you are in crisis, please call or text 988 (US) or contact your local emergency line. I'm here for support, not crisis or emergency care.",
  },
  {
    q: "How do I know which session length to choose?",
    a: "If you're unsure, start with whatever feels right. Reach out first and we'll figure it out together.",
  },
];

export default function ContactPage() {
  usePageMeta({
    title: "Contact Calm & Connected — Send a Note",
    description:
      "Share as much or as little as you'd like. Reach out by note, email, or phone — Maria will reply within a day. Judgment-free peer support starts with a hello.",
  });
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", contact_info: "", needs: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", error: "" });
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    const intent = params.get("intent");
    if (intent) setForm((s) => (s.needs ? s : { ...s, needs: intent }));
  }, [params]);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", error: "" });

    const payload = { ...form };
    if (!payload.needs) delete payload.needs;

    // Try Netlify Forms first. On a Netlify-deployed site this is the
    // primary path (POST / with form-name). In local dev it usually fails
    // silently (no Netlify endpoint), so we then try the FastAPI backend.
    let netlifyOk = false;
    try {
      await submitNetlifyForm("contact", { ...payload, "bot-field": "" });
      netlifyOk = true;
    } catch (_) {
      // fall through to backend
    }

    try {
      if (!netlifyOk) {
        await submitContact(payload);
      }
      setStatus({ state: "success", error: "" });
      setForm({ name: "", contact_info: "", needs: "", message: "" });
    } catch (err) {
      setStatus({
        state: "error",
        error:
          err?.response?.data?.detail?.[0]?.msg ||
          err?.message ||
          "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="pt-36 md:pt-44 pb-12 md:pb-20">
        <div className="cc-container max-w-3xl">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
              Contact
            </p>
            <h1 className="mt-5 font-serif text-5xl md:text-6xl leading-[1.05]">
              Let's talk — start where you are.
            </h1>
            <p className="mt-7 text-lg text-[var(--cc-text-soft)] leading-relaxed">
              You can share as much or as little as you'd like. I'll meet you
              where you are. No pressure. No judgment. Just support.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Contact + Form */}
      <section className="pb-24">
        <div className="cc-container grid md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-5">
            <div className="cc-card p-8 md:p-10 h-full">
              <h3 className="font-serif text-3xl">Reach out directly</h3>
              <p className="mt-3 text-[var(--cc-text-soft)] leading-relaxed">
                Email or text feel a little more personal than a form? Use
                whichever way feels right for you.
              </p>

              <ul className="mt-8 space-y-5">
                <li>
                  <a
                    data-testid="contact-email"
                    href={`mailto:${BRAND.email}`}
                    className="flex items-center gap-3 cc-link"
                  >
                    <span className="w-10 h-10 rounded-full bg-[var(--cc-surface)] border border-[var(--cc-line)] flex items-center justify-center">
                      <Mail size={16} strokeWidth={1.5} className="text-[var(--cc-primary)]" />
                    </span>
                    {BRAND.email}
                  </a>
                </li>
                <li>
                  <a
                    data-testid="contact-phone"
                    href={`tel:${BRAND.phone}`}
                    className="flex items-center gap-3 cc-link"
                  >
                    <span className="w-10 h-10 rounded-full bg-[var(--cc-surface)] border border-[var(--cc-line)] flex items-center justify-center">
                      <Phone size={16} strokeWidth={1.5} className="text-[var(--cc-primary)]" />
                    </span>
                    {BRAND.phoneDisplay}
                  </a>
                </li>
                <li>
                  <a
                    data-testid="contact-tiktok"
                    href={BRAND.socials.tiktok}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 cc-link"
                  >
                    <span
                      className="w-10 h-10 rounded-full border border-[var(--cc-line)] flex items-center justify-center"
                      style={{ background: "rgba(169, 192, 204, 0.18)" }}
                    >
                      <TikTokIcon size={16} className="text-[var(--cc-primary)]" />
                    </span>
                    TikTok · @camilieyes
                  </a>
                </li>
              </ul>

              <div className="mt-10 pt-8 border-t border-[var(--cc-line)] text-sm text-[var(--cc-text-soft)] leading-relaxed">
                <strong className="block text-[var(--cc-text)] font-medium mb-1">
                  A gentle reminder
                </strong>
                If you are in crisis, please call or text 988 (US) right away.
              </div>
            </div>
          </Reveal>

          <Reveal delay={120} className="md:col-span-7">
            <form
              data-testid="contact-form"
              name="contact"
              method="POST"
              data-netlify="true"
              onSubmit={onSubmit}
              className="cc-card p-8 md:p-10"
            >
              <input type="hidden" name="form-name" value="contact" />
              <p className="hidden" aria-hidden="true">
                <label>
                  Don&apos;t fill this out if you&apos;re human:{" "}
                  <input name="bot-field" tabIndex="-1" autoComplete="off" />
                </label>
              </p>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="c-name" className="block text-sm mb-2 text-[var(--cc-text-soft)]">
                    What should I call you?
                  </label>
                  <input
                    id="c-name"
                    name="name"
                    type="text"
                    required
                    data-testid="contact-input-name"
                    className="cc-input"
                    placeholder="Your name (or just a first name)"
                    value={form.name}
                    onChange={update("name")}
                  />
                </div>
                <div>
                  <label htmlFor="c-contact" className="block text-sm mb-2 text-[var(--cc-text-soft)]">
                    How can I reach you?
                  </label>
                  <input
                    id="c-contact"
                    name="contact_info"
                    type="text"
                    required
                    data-testid="contact-input-contact"
                    className="cc-input"
                    placeholder="Email or phone"
                    value={form.contact_info}
                    onChange={update("contact_info")}
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="c-needs" className="block text-sm mb-2 text-[var(--cc-text-soft)]">
                  What would feel most helpful right now?
                </label>
                <input
                  id="c-needs"
                  name="needs"
                  type="text"
                  data-testid="contact-input-needs"
                  className="cc-input"
                  placeholder="Talking, emotional support, feeling overwhelmed, or not sure — anything is okay"
                  value={form.needs}
                  onChange={update("needs")}
                />
              </div>

              <div className="mt-5">
                <label htmlFor="c-message" className="block text-sm mb-2 text-[var(--cc-text-soft)]">
                  What's been on your mind?
                </label>
                <textarea
                  id="c-message"
                  name="message"
                  required
                  data-testid="contact-input-message"
                  className="cc-input"
                  placeholder="Even a few words are okay."
                  value={form.message}
                  onChange={update("message")}
                />
              </div>

              {status.state === "error" && (
                <div
                  data-testid="contact-form-error"
                  className="mt-5 flex items-start gap-2.5 p-4 rounded-2xl bg-[#FBEEEE] border border-[#E8CFCF] text-[#7A3A3A] text-sm"
                >
                  <AlertCircle size={16} strokeWidth={1.7} className="mt-0.5 shrink-0" />
                  {status.error}
                </div>
              )}

              {status.state === "success" && (
                <div
                  data-testid="contact-form-success"
                  className="mt-5 flex items-start gap-2.5 p-4 rounded-2xl bg-[#EEF3EF] border border-[#CFE0D2] text-[#3A5A45] text-sm"
                >
                  <Check size={16} strokeWidth={1.8} className="mt-0.5 shrink-0" />
                  Thank you for reaching out. I'll get back to you as soon as I can.
                </div>
              )}

              <div className="mt-8">
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={status.state === "loading"}
                  className="cc-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status.state === "loading" ? "Sending…" : "Send when you're ready"}
                </button>
              </div>

              <div
                data-testid="contact-privacy-note"
                className="mt-6 flex items-start gap-2.5 text-xs text-[var(--cc-text-soft)]/90 leading-relaxed"
              >
                <Lock size={13} strokeWidth={1.6} className="mt-0.5 text-[var(--cc-primary)] shrink-0" />
                Your note comes only to Maria. Always. There&apos;s no wrong way to start — a few words is enough.
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="cc-section bg-[var(--cc-surface)]">
        <div className="cc-container max-w-3xl">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
              FAQ
            </p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
              A few common questions
            </h2>
          </Reveal>

          <div className="mt-10 divide-y divide-[var(--cc-line)] cc-card p-2 md:p-4">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 80}>
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full text-left px-4 md:px-6 py-5 flex items-center justify-between gap-4"
                >
                  <span className="font-serif text-lg md:text-xl">{f.q}</span>
                  <span className="text-[var(--cc-primary)] text-2xl leading-none">
                    {openFaq === i ? "–" : "+"}
                  </span>
                </button>
                {openFaq === i && (
                  <div data-testid={`faq-answer-${i}`} className="px-4 md:px-6 pb-6 text-[var(--cc-text-soft)] leading-relaxed">
                    {f.a}
                  </div>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
